import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronUp, Link2, SlidersHorizontal } from 'lucide-react'
import { useI18n } from '@/lib/i18n'
import type { EventConfig } from '@/types'
import { effectiveSettings, extractSettings, isInherited, migrateLegacy, type SettingsSection, type SettingsValues } from '../../../server/lib/eventSettings'
import SettingsSectionForm from './SettingsSectionForm'
import { settingsSummary } from '@/lib/settingsPresentation'

export default function InheritedSettings({ section, event, storedEvent, defaults, onChange }: {
  section: SettingsSection; event: EventConfig; storedEvent: EventConfig; defaults: SettingsValues; onChange: (updates: Partial<EventConfig>) => void
}) {
  const { t } = useI18n()
  const inherited = isInherited(storedEvent, section)
  const [open, setOpen] = useState(!inherited)
  const values = effectiveSettings(storedEvent, defaults)
  const title = t(`inheritance.sections.${section}`)
  const setMode = (custom: boolean) => {
    const metadata = migrateLegacy(storedEvent).settingsInheritance!
    if (!custom && !window.confirm(`${t('inheritance.resetConfirm')}\n\n${title}: ${settingsSummary(section, defaults, t)}`)) return
    onChange({ settingsInheritance: { version: 1, sections: { ...metadata.sections, [section]: custom ? { mode: 'custom', value: values[section] } : { mode: 'inherit' } } } })
    setOpen(custom)
  }
  const updateSection = (updates: Partial<EventConfig>) => {
    const next = extractSettings({ ...event, ...updates })
    const metadata = migrateLegacy(storedEvent).settingsInheritance!
    onChange({ settingsInheritance: { version: 1, sections: { ...metadata.sections, [section]: { mode: 'custom', value: next[section] } } } })
  }
  return (
    <section id={section} className="scroll-mt-6 rounded-xl border border-border bg-card/40">
      <div className="flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-semibold">{title}</h2>
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${inherited ? 'bg-primary/10 text-primary' : 'bg-amber-500/10 text-amber-500'}`}>
              {inherited ? <Link2 size={12} /> : <SlidersHorizontal size={12} />}
              {t(inherited ? 'inheritance.general' : 'inheritance.custom')}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">{settingsSummary(section, values, t)}</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs font-medium">
          {inherited ? <>
            <button type="button" onClick={() => setMode(true)} className="rounded-lg border border-border px-3 py-2 hover:bg-muted">{t('inheritance.customize')}</button>
            <Link to={`/admin/settings?section=${section}`} className="px-1 py-2 text-primary hover:underline">{t('inheritance.editGeneral')}</Link>
          </> : <>
            <button type="button" onClick={() => setMode(false)} className="px-1 py-2 text-primary hover:underline">{t('inheritance.useGeneral')}</button>
            <button type="button" aria-expanded={open} aria-controls={`form-${section}`} onClick={() => setOpen(v => !v)} className="flex items-center gap-1 rounded-lg border border-border px-3 py-2 hover:bg-muted">
              {t(open ? 'inheritance.collapse' : 'inheritance.expand')}{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </>}
        </div>
      </div>
      {!inherited && open && <div id={`form-${section}`} className="border-t border-border p-5"><SettingsSectionForm section={section} event={event} onChange={updateSection} /></div>}
    </section>
  )
}
